













import { throttleTime } from 'rxjs/operators/throttleTime';
import { Subscription } from 'rxjs/Subscription';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { EVENTS } from '../../../bus/events.const';
import { logger } from '../../../logger';
import { VueCssClasses } from '../../../utils';

@Component
export default class EmpathizePanel extends Vue {
  protected positionStyle = {};
  protected animatingInput = false;
  protected animatingRoot = false;
  protected resizeSubscription: Subscription;

  @Prop({ default: false })
  open: boolean;

  @Prop()
  domInputSelector: string;

  @Watch('open', { immediate: true })
  onOpenChanged(newValue: boolean) {
    if (newValue) {
      this.updatePositionStyle();
    }
  }

  @Watch('domInput', { immediate: true })
  onDomInputChange(newDomInput: HTMLElement, oldDomInput: HTMLElement) {
    if (newDomInput) {
      this.addEventListeners(newDomInput);
    }
    if (oldDomInput) {
      this.removeEventListeners(oldDomInput);
    }
  }

  mounted() {
    this.resizeSubscription = this.$x.on(EVENTS.WindowSizeChanged)
      .pipe(throttleTime(100, undefined, { leading: true, trailing: true }))
      .subscribe(this.updatePositionStyle);
  }

  beforeDestroy() {
    this.resizeSubscription.unsubscribe();
  }

  protected get showEmpathize(): boolean {
    return this.open && !this.animatingInput && !this.animatingRoot;
  }

  protected get cssClasses(): VueCssClasses {
    return { 'ebx-empathize-panel--open': this.showEmpathize };
  }

  protected addEventListeners(domInput: HTMLElement) {
    if (domInput) {
      domInput.addEventListener('transitionstart', this.animationStart);
      domInput.addEventListener('transitionend', this.animationEnd);
      domInput.addEventListener('animationstart', this.animationStart);
      domInput.addEventListener('animationend', this.animationEnd);
    }
  }

  protected removeEventListeners(domInput: HTMLElement) {
    if (domInput) {
      domInput.removeEventListener('transitionstart', this.animationStart);
      domInput.removeEventListener('transitionend', this.animationEnd);
      domInput.removeEventListener('animationstart', this.animationStart);
      domInput.removeEventListener('animationend', this.animationEnd);
    }
  }

  protected updatePositionStyle() {
    const searchBoxElement = this.getSearchBoxElement();
    if (searchBoxElement) {
      this.positionStyle = this.getPositionStyle(searchBoxElement);
    }
  }

  protected getPositionStyle(searchBoxElement: HTMLElement): Partial<CSSStyleDeclaration> {
    const { height, top, left, width } = searchBoxElement.getBoundingClientRect();
    return {
      top: height + top + 'px',
      left: left + 'px',
      right: `calc(100% - ${ width + left }px)`
    };
  }

  protected getSearchBoxElement(): HTMLElement | null {
    const searchBoxElement = this.getSearchBoxElementFromSmartComponent() || this.getSearchBoxElementFromDocument();
    if (!searchBoxElement && this.domInputSelector) {
      logger.warn('[EmpathizePanel]', `We couldn't find a search-box for the selector "${ this.domInputSelector }"`);
    }
    return searchBoxElement;
  }

  protected getSearchBoxElementFromSmartComponent(): HTMLElement | null {
    const searchBox = this.$x.smart.SearchBox;
    if (searchBox && searchBox.$el) {
      const searchBoxElement = searchBox.$el as HTMLElement;
      return this.domInputSelector
        ? searchBoxElement.querySelector(this.domInputSelector)
        : searchBoxElement;
    }
    return null;
  }

  protected getSearchBoxElementFromDocument(): HTMLElement | null {
    return (this.domInputSelector && document.querySelector(this.domInputSelector));
  }

  protected animationStart() {
    this.animatingInput = true;
  }

  protected animationEnd() {
    this.updatePositionStyle();
    this.animatingInput = false;
  }

  onScroll() {
    this.$x.emit(EVENTS.EmpathizeScroll);
  }
}
